import { useState, useRef, useCallback } from 'react'
import { ScrollView, StyleSheet, View, Platform } from 'react-native'
import { Link } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '../../../../app/design-system'
import { useStore } from '../../../../app/models'
import { SectionTitle } from '../../../../app/components/Admin/SectionTitle'
import { LoungeAvatar } from '../../../../app/components/LoungeAvatar'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Text } from 'react-native'

type Member = {
  userId: string
  role: string
  status: string
  joinedAt: number
  isOnline: boolean
}

const columnHelper = createColumnHelper<Member>()

const memberColumns = [
  columnHelper.accessor('userId', {
    header: 'User ID',
    cell: (info) => <DS.Text selectable variant="Body Small/Regular">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('role', {
    header: 'Role',
    cell: (info) => <DS.Text variant="Body Small/Medium">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => <DS.Text variant="Body Small/Regular">{info.getValue()}</DS.Text>
  }),
  columnHelper.accessor('isOnline', {
    header: 'Online',
    cell: (info) => (
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: info.getValue() ? '#22c55e' : '#d1d5db'
        }}
      />
    )
  })
]

function resizeImageToBlob(
  file: File,
  size: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context unavailable'))
        ctx.drawImage(img, 0, 0, size, size)
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
          'image/webp',
          0.85
        )
      }
      img.onerror = reject
      img.src = reader.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export default function LoungesAdmin() {
  const store = useStore()
  const userId = store.session.user?.auth0Id ?? ''

  const allLounges = useQuery(api.lounges.queries.getAllLoungesAdmin)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const loungeId = selectedId as Id<'lounges'> | null

  const lobby = useQuery(
    api.lounges.queries.getLoungeLobby,
    loungeId ? { loungeId, userId } : 'skip'
  )

  const members = useQuery(
    api.lounges.queries.getLoungeMembers,
    loungeId ? { loungeId } : 'skip'
  )

  const rotateCode = useMutation(api.lounges.mutations.rotateLoungeJoinCode)
  const adminUpdateBranding = useMutation(api.lounges.mutations.adminUpdateLoungeBranding)

  const [rotating, setRotating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const selectedLounge = allLounges?.find((l) => l._id === selectedId) ?? null

  const memberRows = (members ?? []) as Member[]
  const table = useReactTable({ data: memberRows, columns: memberColumns, getCoreRowModel: getCoreRowModel() })

  const handleLogoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !loungeId) return
      setUploading(true)
      try {
        const [logoBlob, thumbBlob] = await Promise.all([
          resizeImageToBlob(file, 400),
          resizeImageToBlob(file, 80)
        ])

        const res = await fetch(`/api/lounges/logo-upload-urls?loungeId=${loungeId}`)
        const { logoUrl, thumbUrl, logoS3Key, thumbS3Key } = await res.json()

        await Promise.all([
          fetch(logoUrl, { method: 'PUT', body: logoBlob, headers: { 'Content-Type': 'image/webp' } }),
          fetch(thumbUrl, { method: 'PUT', body: thumbBlob, headers: { 'Content-Type': 'image/webp' } })
        ])

        await adminUpdateBranding({ loungeId, branding: { logoS3Key, logoThumbS3Key: thumbS3Key } })
      } catch (err) {
        console.error('Logo upload failed', err)
      } finally {
        setUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [loungeId, adminUpdateBranding]
  )

  if (!allLounges) {
    return (
      <View style={styles.container}>
        <DS.Text variant="Body Large/Regular">Loading lounges...</DS.Text>
      </View>
    )
  }

  return (
    <ScrollView>
      <View style={styles.container}>
        <SectionTitle title="Lounges" subtitle={`${allLounges.length} total`} />

        {/* Lounge Picker */}
        {Platform.OS === 'web' && (
          <View style={styles.card}>
            <select
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(e.target.value || null)}
              style={{ padding: 8, fontSize: 16, borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
            >
              <option value="">Select a lounge...</option>
              {allLounges.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.name} ({l.status})
                </option>
              ))}
            </select>
          </View>
        )}

        {selectedLounge && lobby && (
          <>
            {/* Branding */}
            <View style={styles.section}>
              <DS.Text variant="Heading 3/Medium">Branding</DS.Text>
              <View style={styles.card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                  <LoungeAvatar name={selectedLounge.name} branding={selectedLounge.branding} size={80} />
                  <View style={{ gap: 8 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      style={{ fontSize: 14 }}
                    />
                    {uploading && (
                      <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                        Uploading...
                      </DS.Text>
                    )}
                  </View>
                </View>
              </View>
            </View>

            {/* Settings */}
            <View style={styles.section}>
              <DS.Text variant="Heading 3/Medium">Settings</DS.Text>
              <View style={styles.card}>
                <DS.Text variant="Body Large/Regular">Chat enabled: {lobby.settings.chatEnabled ? 'Yes' : 'No'}</DS.Text>
                <DS.Text variant="Body Large/Regular">Ad-hoc games: {lobby.settings.adHocGamesEnabled ? 'Yes' : 'No'}</DS.Text>
                <DS.Text variant="Body Large/Regular">Max members: {lobby.settings.maxMembers}</DS.Text>
                <DS.Text variant="Body Large/Regular">Chat retention: {lobby.settings.chatRetentionDays} days</DS.Text>
              </View>
            </View>

            {/* Members */}
            <View style={styles.section}>
              <DS.Text variant="Heading 3/Medium">Members ({memberRows.length})</DS.Text>
              <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                  {table.getHeaderGroups().map((hg) => (
                    <View style={styles.tableRow} key={hg.id}>
                      {hg.headers.map((h) => (
                        <View style={styles.tableHeaderCell} key={h.id}>
                          <Text style={styles.headerText}>
                            {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
                <View>
                  {table.getRowModel().rows.map((row) => (
                    <View style={styles.tableRow} key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <View style={styles.tableCell} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            </View>

            {/* Actions */}
            <View style={styles.section}>
              <DS.Text variant="Heading 3/Medium">Actions</DS.Text>
              <View style={styles.card}>
                {lobby.joinCode && (
                  <DS.Text selectable variant="Body Large/Regular">
                    Join code: {lobby.joinCode}
                  </DS.Text>
                )}

                <DS.Button
                  type="On Dark"
                  variant="Medium/Secondary"
                  onPress={async () => {
                    if (!loungeId) return
                    setRotating(true)
                    try {
                      await rotateCode({ loungeId, actorId: userId })
                    } finally {
                      setRotating(false)
                    }
                  }}
                  disabled={rotating}
                >
                  Rotate Join Code
                </DS.Button>

                <Link href={`/dashboard/lounges/${selectedId}/report` as any}>
                  <DS.Text variant="Body Large/Medium" style={{ color: DS.palette['Brand/Maroon'] }}>
                    View Report →
                  </DS.Text>
                </Link>
              </View>
            </View>
          </>
        )}
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    gap: 24
  },
  section: {
    gap: 12
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    gap: 8,
    borderWidth: 1,
    borderColor: '#e9ecef'
  },
  tableContainer: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e9ecef'
  },
  tableHeader: {
    backgroundColor: '#eeedea'
  },
  tableRow: {
    flexDirection: 'row'
  },
  tableHeaderCell: {
    flex: 1,
    padding: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#dee2e6'
  },
  headerText: {
    fontWeight: '600',
    color: '#6b7280'
  },
  tableCell: {
    flex: 1,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6'
  }
})
